/**
 * VpnPage (F24.3) — apartado dedicado al módulo VPN.
 *
 * Layout:
 *   - 4 stat cards: Conectados / Desconectados / Sin asignar / Total
 *   - Filtros: search + estado + sin_router
 *   - Tabla con columnas: Descripción, Usuario, Vinculado a, IP túnel,
 *     Estado, Última conexión, Acciones (Ver snippet, Descargar .ovpn,
 *     Editar, Regenerar, Eliminar)
 *   - Auto-refresh 30s para ver estado en vivo
 *   - Botón "Nueva credencial" arriba a la derecha
 */
import { useMemo, useState } from "react";
import {
  KeyRound, Plus, Search, Wifi, WifiOff, Clock, RefreshCw, Trash2,
  Pencil, Download, Terminal, Filter,
} from "lucide-react";
import { toast } from "sonner";

import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";

import {
  descargarOvpnConfig, useBorrarCredencial, useOvpnCredenciales,
  useOvpnDashboard, useRegenerarPassword,
} from "./api";
import type { OvpnCredencial, OvpnEstadoFilter } from "./schemas";
import { VpnFormDialog } from "./VpnFormDialog";
import { VpnSnippetDialog } from "./VpnSnippetDialog";
import { VpnCredencialesGeneradasModal } from "./VpnCredencialesGeneradasModal";
import type { OvpnCredencialesGeneradasResponse } from "./schemas";

export function VpnPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [estado, setEstado] = useState<OvpnEstadoFilter | null>(null);
  const [sinRouter, setSinRouter] = useState<boolean | null>(null);

  const filters = useMemo(
    () => ({ page, size: 30, search, estado, sin_router: sinRouter }),
    [page, search, estado, sinRouter],
  );
  const { data, isLoading, isFetching } = useOvpnCredenciales(filters);
  const { data: stats } = useOvpnDashboard();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingCred, setEditingCred] = useState<OvpnCredencial | null>(null);
  const [snippetCredId, setSnippetCredId] = useState<string | null>(null);
  const [credShow, setCredShow] = useState<OvpnCredencialesGeneradasResponse | null>(null);

  const regenerarMut = useRegenerarPassword();
  const borrarMut = useBorrarCredencial();

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleRegenerar(c: OvpnCredencial) {
    if (!confirm(
      `¿Rotar password de "${c.descripcion}"?\n\n` +
      "La sesión actual queda inválida — el dispositivo debe pegar el nuevo " +
      "snippet o cargar el .ovpn fresco para reconectar.",
    )) return;
    try {
      const res = await regenerarMut.mutateAsync(c.id);
      setCredShow(res);
      toast.success("Password rotada");
    } catch (e) {
      toast.error("No se pudo regenerar", { description: (e as ApiError).message });
    }
  }

  async function handleBorrar(c: OvpnCredencial) {
    if (!confirm(
      `¿Eliminar la credencial "${c.descripcion}" (${c.ovpn_user})?\n\n` +
      "Se borra del sistema. El dispositivo conectado se desconecta. " +
      (c.router_id ? "El router asociado queda sin VPN." : ""),
    )) return;
    try {
      await borrarMut.mutateAsync(c.id);
      toast.success("Credencial eliminada");
    } catch (e) {
      toast.error("No se pudo eliminar", { description: (e as ApiError).message });
    }
  }

  async function handleDescargar(c: OvpnCredencial) {
    try {
      await descargarOvpnConfig(c.id, `byteisp-${c.ovpn_user.toLowerCase()}.ovpn`);
      toast.success(".ovpn descargado");
    } catch (e) {
      toast.error("No se pudo descargar", { description: (e as Error).message });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <KeyRound size={16} /> VPN
          </h2>
          <p className="text-xs text-muted-foreground">
            Credenciales OpenVPN para routers, técnicos y equipos de soporte.
            Genera una credencial, copia el snippet y pega en el dispositivo.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Nueva credencial
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <StatCard
          icon={<Wifi size={14} className="text-emerald-500" />}
          label="Conectados ahora"
          value={stats?.conectados_ahora ?? 0}
          accent="emerald"
          onClick={() => { setEstado("conectado"); setPage(1); }}
        />
        <StatCard
          icon={<WifiOff size={14} className="text-muted-foreground" />}
          label="Desconectados"
          value={stats?.desconectados ?? 0}
          onClick={() => { setEstado("desconectado"); setPage(1); }}
        />
        <StatCard
          icon={<Clock size={14} className="text-amber-500" />}
          label="Nunca conectados"
          value={stats?.nunca_conectados ?? 0}
          onClick={() => { setEstado("nunca"); setPage(1); }}
        />
        <StatCard
          icon={<KeyRound size={14} className="text-primary" />}
          label="Total credenciales"
          value={stats?.total ?? 0}
          onClick={() => { setEstado(null); setSinRouter(null); setPage(1); }}
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Buscar por descripción, usuario o router…"
            className="pl-8"
          />
        </div>
        <FilterPills
          activeEstado={estado}
          onEstado={(e) => { setEstado(e); setPage(1); }}
          activeSinRouter={sinRouter}
          onSinRouter={(v) => { setSinRouter(v); setPage(1); }}
        />
      </div>

      {/* Tabla */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estado</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Vinculado a</TableHead>
              <TableHead>IP túnel</TableHead>
              <TableHead>Última conexión</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={KeyRound}
                  title="Sin credenciales"
                  description={
                    search || estado || sinRouter !== null
                      ? "Sin coincidencias con los filtros."
                      : "Crea la primera credencial OVPN."
                  }
                  action={
                    !search && !estado && sinRouter === null ? (
                      <Button size="sm" onClick={() => setCreateOpen(true)}>
                        <Plus size={14} /> Nueva credencial
                      </Button>
                    ) : undefined
                  }
                />
              </TableEmpty>
            ) : (
              items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    {c.ovpn_conectado_ahora ? (
                      <Badge variant="success" className="gap-1">
                        <Wifi size={10} /> En vivo
                      </Badge>
                    ) : !c.ovpn_last_seen_at ? (
                      <Badge variant="secondary" className="gap-1 opacity-60">
                        Nunca
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <WifiOff size={10} /> Off
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {c.descripcion}
                    {!c.activo && (
                      <Badge variant="secondary" className="ml-1 text-[9px]">
                        inactiva
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.ovpn_user}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.router_nombre ?? <span className="italic">— Sin asignar</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.ovpn_ip_tunel ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {c.ovpn_last_seen_at ? formatDateTime(c.ovpn_last_seen_at) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => setSnippetCredId(c.id)}
                        title="Ver snippet RouterOS / .ovpn"
                      >
                        <Terminal size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDescargar(c)}
                        title="Descargar .ovpn"
                      >
                        <Download size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => setEditingCred(c)}
                        title="Editar (descripción, vinculación, activo)"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleRegenerar(c)}
                        disabled={regenerarMut.isPending}
                        title="Regenerar password"
                      >
                        <RefreshCw size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleBorrar(c)}
                        disabled={borrarMut.isPending}
                        title="Eliminar"
                      >
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
              {isFetching && " · actualizando…"}
            </span>
            <div className="flex gap-1">
              <Button
                size="sm" variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >Anterior</Button>
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >Siguiente</Button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <VpnFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        credencial={null}
        onCreated={(res) => { setCreateOpen(false); setCredShow(res); }}
      />
      <VpnFormDialog
        open={editingCred !== null}
        onOpenChange={(v) => { if (!v) setEditingCred(null); }}
        credencial={editingCred}
        onCreated={() => { /* edit no muestra password */ }}
      />
      <VpnSnippetDialog
        credencialId={snippetCredId}
        open={snippetCredId !== null}
        onOpenChange={(v) => { if (!v) setSnippetCredId(null); }}
      />
      <VpnCredencialesGeneradasModal
        cred={credShow}
        onClose={() => setCredShow(null)}
      />
    </div>
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────────────

function StatCard({
  icon, label, value, onClick, accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  onClick?: () => void;
  accent?: "emerald";
}) {
  const accentClass = accent === "emerald"
    ? "text-emerald-600 dark:text-emerald-400"
    : "";
  return (
    <button
      onClick={onClick}
      className="flex flex-col gap-1 rounded-lg border bg-card p-3 text-left hover:bg-muted/30 transition"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-semibold ${accentClass}`}>{value}</div>
    </button>
  );
}

function FilterPills({
  activeEstado, onEstado, activeSinRouter, onSinRouter,
}: {
  activeEstado: OvpnEstadoFilter | null;
  onEstado: (e: OvpnEstadoFilter | null) => void;
  activeSinRouter: boolean | null;
  onSinRouter: (v: boolean | null) => void;
}) {
  const Pill = ({
    label, active, onClick,
  }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
        active ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/30"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex items-center gap-1.5">
      <Filter size={12} className="text-muted-foreground" />
      <Pill
        label="Conectadas"
        active={activeEstado === "conectado"}
        onClick={() => onEstado(activeEstado === "conectado" ? null : "conectado")}
      />
      <Pill
        label="Desconectadas"
        active={activeEstado === "desconectado"}
        onClick={() => onEstado(activeEstado === "desconectado" ? null : "desconectado")}
      />
      <Pill
        label="Nunca"
        active={activeEstado === "nunca"}
        onClick={() => onEstado(activeEstado === "nunca" ? null : "nunca")}
      />
      <span className="mx-1 text-muted-foreground/40">·</span>
      <Pill
        label="Sin router"
        active={activeSinRouter === true}
        onClick={() => onSinRouter(activeSinRouter === true ? null : true)}
      />
    </div>
  );
}
